import React, { useState, useMemo, useEffect } from 'react';
import type { PrintableDocGroup } from '../utils/adminSync';

export interface PrintableDocInfo {
  id: string;
  docNumber?: string;
  category?: string;
  title: string;
  fullName?: string;
  pdfUrl?: string;
  docUrl?: string;
}

interface AddToGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: PrintableDocInfo | null;
  groups: PrintableDocGroup[];
  onSaveGroup: (group: PrintableDocGroup) => Promise<void>;
  onDeleteGroup?: (groupId: string) => Promise<void>;
  currentUserEmail?: string;
  currentUserName?: string;
  initialEditingGroupId?: string | null;
}

const LEGACY_DOC_ID_ALIASES: Record<string, string> = {
  'print-doc-94': 'print-doc-128',
  'print-doc-95': 'print-doc-127',
  'print-doc-96': 'print-doc-126',
  'print-doc-97': 'print-doc-125',
  'print-doc-99': 'print-doc-124',
};

export const AddToGroupModal: React.FC<AddToGroupModalProps> = ({
  isOpen,
  onClose,
  document,
  groups,
  onSaveGroup,
  onDeleteGroup,
  currentUserEmail,
  currentUserName,
  initialEditingGroupId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupVisibility, setNewGroupVisibility] = useState<'public' | 'private'>('public');
  const [addDocToNewGroup, setAddDocToNewGroup] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingToggles, setPendingToggles] = useState<Record<string, boolean>>({});
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Group editing state
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editVisibility, setEditVisibility] = useState<'public' | 'private'>('public');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Set initial editing group if passed
  useEffect(() => {
    if (isOpen && initialEditingGroupId) {
      const g = groups.find(item => item.id === initialEditingGroupId);
      if (g) {
        setEditingGroupId(g.id);
        setEditName(g.name);
        setEditVisibility(g.visibility === 'private' ? 'private' : 'public');
      }
    } else if (isOpen) {
      setEditingGroupId(null);
      setConfirmDeleteId(null);
    }
  }, [isOpen, initialEditingGroupId, groups]);

  // Filter groups by search query
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;
    const q = searchTerm.toLowerCase().trim();
    return groups.filter(g => g.name.toLowerCase().includes(q));
  }, [groups, searchTerm]);

  if (!isOpen) return null;

  // Toggle document in/out of an existing group
  const handleToggleGroup = async (group: PrintableDocGroup) => {
    if (!document) return;
    setPendingToggles(prev => ({ ...prev, [group.id]: true }));
    setStatusMessage(null);

    try {
      const currentIds = Array.isArray(group.documentIds) ? [...group.documentIds] : [];
      const isDocInGroup = (id: string) => {
        const resolved = LEGACY_DOC_ID_ALIASES[id] || id;
        const targetResolved = LEGACY_DOC_ID_ALIASES[document.id] || document.id;
        return resolved === targetResolved || id === document.id;
      };
      const exists = currentIds.some(isDocInGroup);
      const updatedDocIds = exists
        ? currentIds.filter(id => !isDocInGroup(id))
        : [...currentIds, document.id];

      const updatedGroup: PrintableDocGroup = {
        ...group,
        documentIds: updatedDocIds,
        updatedAt: Date.now(),
      };

      await onSaveGroup(updatedGroup);
      setStatusMessage({
        text: exists
          ? `Removed from "${group.name}"`
          : `Added to "${group.name}"`,
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to update group assignment:', err);
      setStatusMessage({
        text: 'Failed to update group. Please try again.',
        type: 'error',
      });
    } finally {
      setPendingToggles(prev => ({ ...prev, [group.id]: false }));
    }
  };

  // Start editing a group
  const startEditingGroup = (group: PrintableDocGroup) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
    setEditVisibility(group.visibility === 'private' ? 'private' : 'public');
    setConfirmDeleteId(null);
    setStatusMessage(null);
  };

  // Save edited group
  const handleSaveEdit = async (group: PrintableDocGroup) => {
    const trimmed = editName.trim();
    if (!trimmed) return;

    setIsSavingEdit(true);
    setStatusMessage(null);

    try {
      const updated: PrintableDocGroup = {
        ...group,
        name: trimmed,
        visibility: editVisibility,
        updatedAt: Date.now(),
      };
      await onSaveGroup(updated);
      setEditingGroupId(null);
      setStatusMessage({
        text: `Updated group "${trimmed}" successfully!`,
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to update group:', err);
      setStatusMessage({
        text: 'Failed to save changes to group.',
        type: 'error',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Delete a group
  const handleDeleteGroup = async (group: PrintableDocGroup) => {
    if (!onDeleteGroup) return;
    setIsSavingEdit(true);
    setStatusMessage(null);

    try {
      await onDeleteGroup(group.id);
      setEditingGroupId(null);
      setConfirmDeleteId(null);
      setStatusMessage({
        text: `Deleted group "${group.name}".`,
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to delete group:', err);
      setStatusMessage({
        text: 'Failed to delete group. Please try again.',
        type: 'error',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Create a brand new group
  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = newGroupName.trim();
    if (!trimmedName) return;

    setIsCreating(true);
    setStatusMessage(null);

    try {
      const newGroup: PrintableDocGroup = {
        id: `doc-grp-${Date.now()}`,
        name: trimmedName,
        archived: false,
        documentIds: document && addDocToNewGroup ? [document.id] : [],
        createdBy: currentUserEmail || undefined,
        createdByName: currentUserName || (currentUserEmail ? currentUserEmail.split('@')[0] : undefined),
        visibility: newGroupVisibility,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await onSaveGroup(newGroup);
      setNewGroupName('');
      setStatusMessage({
        text: `Created group "${trimmedName}" successfully!`,
        type: 'success',
      });
    } catch (err) {
      console.error('Failed to create group:', err);
      setStatusMessage({
        text: 'Failed to create group. Please try again.',
        type: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-150">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 max-w-lg w-full p-5 space-y-4 shadow-2xl text-gray-900 dark:text-gray-100 flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex justify-between items-start border-b border-gray-200 dark:border-gray-700 pb-3 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl p-2 bg-emerald-100 dark:bg-emerald-950/60 rounded-xl text-emerald-600 dark:text-emerald-400">
              📁
            </span>
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                {document ? 'Add Document to Group' : 'Manage & Edit Groups'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Organize, rename, edit, or create printable document groups
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-lg font-bold p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Selected Document Info Banner */}
        {document && (
          <div className="p-3 bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-xl flex-shrink-0 space-y-1">
            <div className="flex items-center justify-between text-[11px] font-bold text-blue-700 dark:text-blue-300">
              <span className="uppercase tracking-wider">
                {document.category || 'Printable Document'}
              </span>
              {document.docNumber && (
                <span className="bg-blue-100 dark:bg-blue-900/60 px-2 py-0.5 rounded-full">
                  Form #{document.docNumber}
                </span>
              )}
            </div>
            <div className="font-bold text-sm text-gray-900 dark:text-white truncate">
              {document.title}
            </div>
          </div>
        )}

        {/* Status Notification */}
        {statusMessage && (
          <div
            className={`p-2.5 rounded-xl text-xs font-bold flex items-center justify-between flex-shrink-0 ${
              statusMessage.type === 'success'
                ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-300'
                : 'bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <span>{statusMessage.type === 'success' ? '✓' : '⚠️'}</span>
              <span>{statusMessage.text}</span>
            </div>
            <button
              onClick={() => setStatusMessage(null)}
              className="text-xs opacity-70 hover:opacity-100 cursor-pointer"
            >
              ✕
            </button>
          </div>
        )}

        {/* Existing Groups Section */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-[140px] pr-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Existing Groups ({groups.length})
            </label>
            {groups.length > 4 && (
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Filter groups..."
                className="text-xs px-2 py-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 w-36"
              />
            )}
          </div>

          {groups.length === 0 ? (
            <div className="text-center py-6 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No groups created yet. Create your first group below!
              </p>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-4 text-xs text-gray-400">
              No groups match &ldquo;{searchTerm}&rdquo;
            </div>
          ) : (
            <div className="space-y-2">
              {filteredGroups.map(g => {
                const isSelected = document
                  ? Array.isArray(g.documentIds) && g.documentIds.some(id => (LEGACY_DOC_ID_ALIASES[id] || id) === (LEGACY_DOC_ID_ALIASES[document.id] || document.id))
                  : false;
                const isToggling = pendingToggles[g.id];
                const isEditing = editingGroupId === g.id;

                // Render Edit Panel if this group is being edited
                if (isEditing) {
                  return (
                    <div
                      key={g.id}
                      className="p-3 bg-blue-50/80 dark:bg-blue-950/40 border border-blue-300 dark:border-blue-700 rounded-xl space-y-2.5 shadow-sm animate-in fade-in duration-100"
                    >
                      <div className="flex items-center justify-between text-xs font-bold text-blue-900 dark:text-blue-200">
                        <span className="flex items-center gap-1.5">
                          <span>✏️</span> Edit Group: <span className="font-mono text-[11px] truncate max-w-[200px]">{g.name}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditingGroupId(null)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-white cursor-pointer"
                          title="Cancel editing"
                        >
                          ✕
                        </button>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
                          Group Name
                        </label>
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          placeholder="Group name"
                          className="w-full px-2.5 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-xs font-semibold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">Visibility:</span>
                          <select
                            value={editVisibility}
                            onChange={e => setEditVisibility(e.target.value as 'public' | 'private')}
                            className="text-[11px] font-semibold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-800 dark:text-gray-200 outline-none cursor-pointer"
                          >
                            <option value="public">🌐 Everyone (Public)</option>
                            <option value="private">🔒 Only Me (Private)</option>
                          </select>
                        </div>

                        <div className="flex items-center gap-1.5 ml-auto">
                          {onDeleteGroup && (
                            confirmDeleteId === g.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteGroup(g)}
                                  disabled={isSavingEdit}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
                                >
                                  Confirm Delete
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteId(null)}
                                  className="px-1.5 py-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 text-[11px] cursor-pointer"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(g.id)}
                                className="px-2 py-1 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white dark:bg-red-950/40 dark:hover:bg-red-600 dark:text-red-300 dark:hover:text-white text-[11px] font-bold rounded-lg border border-red-200 dark:border-red-800 transition-colors cursor-pointer"
                                title="Delete this group"
                              >
                                🗑️ Delete
                              </button>
                            )
                          )}

                          <button
                            type="button"
                            onClick={() => handleSaveEdit(g)}
                            disabled={isSavingEdit || !editName.trim()}
                            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-xs font-bold rounded-lg shadow-sm transition-all cursor-pointer"
                          >
                            {isSavingEdit ? 'Saving...' : '✓ Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Standard Group Row
                return (
                  <div
                    key={g.id}
                    className={`p-2.5 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'bg-blue-50/60 dark:bg-blue-950/40 border-blue-400 dark:border-blue-600'
                        : 'bg-gray-50/70 dark:bg-gray-900/60 border-gray-200 dark:border-gray-700/70 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div
                      onClick={() => {
                        if (document && !isToggling) handleToggleGroup(g);
                      }}
                      className={`flex items-center gap-2.5 min-w-0 flex-1 ${
                        document ? 'cursor-pointer' : ''
                      }`}
                    >
                      {document && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isToggling}
                          onChange={() => {}}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 cursor-pointer pointer-events-none"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-xs text-gray-900 dark:text-white truncate">
                            {g.name}
                          </span>
                          {g.visibility === 'private' && (
                            <span className="text-[10px] bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 font-bold px-1.5 py-0.2 rounded">
                              Private
                            </span>
                          )}
                        </div>
                        {g.createdByName && (
                          <p className="text-[10px] text-gray-400 truncate">
                            Created by: {g.createdByName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-700">
                        {(g.documentIds || []).length} forms
                      </span>
                      {document && isSelected && (
                        <button
                          type="button"
                          disabled={isToggling}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleGroup(g);
                          }}
                          className="px-2 py-0.5 rounded text-[11px] font-bold text-rose-600 hover:text-white hover:bg-rose-600 dark:text-rose-400 dark:hover:text-white dark:hover:bg-rose-600 border border-rose-300 dark:border-rose-700 transition-colors cursor-pointer flex items-center gap-1 shadow-sm"
                          title={`Remove "${document.title}" from "${g.name}"`}
                        >
                          ✕ Remove
                        </button>
                      )}
                      {document && !isSelected && (
                        <button
                          type="button"
                          disabled={isToggling}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleGroup(g);
                          }}
                          className="px-2 py-0.5 rounded text-[11px] font-bold text-emerald-600 hover:text-white hover:bg-emerald-600 dark:text-emerald-400 dark:hover:text-white dark:hover:bg-emerald-600 border border-emerald-300 dark:border-emerald-700 transition-colors cursor-pointer flex items-center gap-1 shadow-sm"
                          title={`Add "${document.title}" to "${g.name}"`}
                        >
                          + Add
                        </button>
                      )}
                      {isToggling && (
                        <span className="text-xs text-blue-500 animate-spin">⟳</span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingGroup(g);
                        }}
                        className="p-1 rounded-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-200 dark:hover:bg-gray-700 text-xs transition-colors cursor-pointer"
                        title="Edit group name and settings"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create New Group Form */}
        <form
          onSubmit={handleCreateGroup}
          className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2.5 flex-shrink-0"
        >
          <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
            + Create a New Group
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              placeholder="e.g. Front Desk Packets, Surgical Forms..."
              className="flex-1 px-3 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl text-xs font-semibold text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition-all shadow-sm"
            />
            <button
              type="submit"
              disabled={isCreating || !newGroupName.trim()}
              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-xs font-bold rounded-xl shadow-sm transition-all cursor-pointer flex-shrink-0 flex items-center gap-1 hover:scale-105 active:scale-95 disabled:hover:scale-100"
            >
              {isCreating ? 'Creating...' : '+ Create Group'}
            </button>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 pt-0.5">
            {document && (
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={addDocToNewGroup}
                  onChange={e => setAddDocToNewGroup(e.target.checked)}
                  className="w-3.5 h-3.5 rounded text-emerald-600 border-gray-300 dark:border-gray-600 focus:ring-emerald-500"
                />
                <span>Add this document to new group</span>
              </label>
            )}

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[10px] uppercase font-bold text-gray-400">Visibility:</span>
              <select
                value={newGroupVisibility}
                onChange={e => setNewGroupVisibility(e.target.value as 'public' | 'private')}
                className="text-[11px] font-semibold bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-0.5 text-gray-700 dark:text-gray-300 outline-none cursor-pointer"
              >
                <option value="public">🌐 Everyone (Public)</option>
                <option value="private">🔒 Only Me (Private)</option>
              </select>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all cursor-pointer hover:scale-105 active:scale-95"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
